import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { LogoWithAppName } from '@/pages/Onboarding/components/LogoWithAppName';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import {
  OnboardingPreferencesList,
  OnboardingPreferencesOption,
} from '@/pages/Onboarding/components/PreferencesList';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { AI_DATA } from '@/pages/Onboarding/constants';
import { useOnboarding } from '@/pages/Onboarding/context';
import { useQueryOnboardingCategories } from '@/pages/Onboarding/hooks/useQueryOnboardingCategories';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';

import './Preferences.scss';

const HARDCODED_REAL_PEOPLE_AI_ID = '7ffe5dd5-3a4b-42b8-9817-62fce9ab152e';

export const ScreenPreferences: React.FC = () => {
  const { user } = useAccount();

  React.useEffect(() => {
    if (user && canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Twin Preferences',
      });
    }
    preloadImage(AI_DATA[webCampaign.aiType].noLimit.bgImageSrc);
  }, [user]);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const categories = form.watch('categories');
  const preloadCategories = form.watch('preloadCategories');
  // PROD-2437 - Remove Real People AI from the list of categories. Crutch for now.
  const filteredPreloadCategories = preloadCategories.filter(
    (category) => category.id !== HARDCODED_REAL_PEOPLE_AI_ID,
  );

  const handleNextStep = () => {
    userService.createOrUpdate({
      categories,
      onboarding_data: {
        first_categories: categories,
      },
    });
    wizard.nextStep();
  };

  useQueryOnboardingCategories({
    enabled: preloadCategories.length === 0,
    form,
  });

  return (
    <OnboardingPage className="onboarding-preferences">
      <header>
        <LogoWithAppName />
        <OnboardingTitle color="black">
          Who you want to chat with?
        </OnboardingTitle>
        <OnboardingSubtitle color="neutral-black">
          You can select more than one option:
        </OnboardingSubtitle>
      </header>

      <IonContent>
        <OnboardingPreferencesList>
          {filteredPreloadCategories &&
            filteredPreloadCategories.map((option) => {
              const active = categories?.includes(option.id);

              return (
                <OnboardingPreferencesOption
                  form={form}
                  active={active}
                  option={option}
                  key={option.id}
                  shape="square"
                />
              );
            })}
        </OnboardingPreferencesList>
      </IonContent>

      <OnboardingFooter
        onContinue={handleNextStep}
        disabled={categories.length === 0}
      />
    </OnboardingPage>
  );
};
